import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useDisclosure } from '@aimsos/hooks';
import Button from './Button.jsx';

/**
 * @param {'small'|'medium'|'large'|'sm'|'md'|'lg'} s
 * @returns {'sm'|'md'|'lg'}
 */
function annSizeToButtonSize(s) {
  if (s === 'small' || s === 'sm') return 'sm';
  if (s === 'large' || s === 'lg') return 'lg';
  return 'md';
}

/** Estilos compartidos del panel; la posición va aparte (`absolute` vs `fixed` con portal). */
const menuPanelSurfaceClass =
  'min-w-[10rem] rounded-lg border border-edge bg-surface py-1 shadow-[0px_4px_12px_rgba(0,0,0,0.08)]';

const menuPanelBaseClass = ['absolute z-[1000]', menuPanelSurfaceClass].join(' ');

/** Portal: solo `fixed` (no mezclar con `absolute` en la misma cadena — orden en CSS de Tailwind impredecible). */
const menuPanelPortalClass = ['fixed z-[11000]', menuPanelSurfaceClass].join(' ');

/** Menu rows the keyboard can reach: a native button counts even without a tabindex. */
const FOCUSABLE_ITEM =
  '[role^="menuitem"]:not([disabled]):not([aria-disabled="true"]):not([tabindex="-1"])';
const TRIGGER_FOCUSABLE = 'button, [href], [tabindex]:not([tabindex="-1"])';
const TEXT_FIELD = 'input, textarea, select, [contenteditable="true"]';

/**
 * Panel cerrado: no se desmonta el árbol de `children` (solo se oculta con CSS).
 * Evita montar/desmontar en cada apertura — menos churn en Chrome y mejor si el menú es pesado.
 * Misma idea que muchos menús headless (estado visual, nodo persistente en el DOM).
 */
const menuOpenClass = 'visible opacity-100';
const menuClosedClass =
  'invisible pointer-events-none opacity-0 select-none';

const DropdownMenuContext = createContext(null);

/**
 * Menú desplegable (paridad con ann `ButtonMenu` / `button-menu`).
 *
 * **Dos modos:**
 * - **`trigger`**: pasas el botón o control (composición total; se ignoran `label` y props de botón interno).
 * - **`label`**: sin `trigger`, se renderiza un `Button` (`variant`, `size`, `pill`, `disabled`, chevron / `triggerIcon`).
 *
 * @param {object} props
 * @param {React.ReactNode} [props.trigger]
 * @param {string} [props.label]
 * @param {string} [props.variant='primary'] — Mismas variantes que `Button`.
 * @param {'small'|'medium'|'large'|'sm'|'md'|'lg'} [props.size='medium']
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.pill]
 * @param {'top'|'bottom'} [props.menuPosition='bottom']
 * @param {'left'|'right'} [props.alignMenu]
 * @param {'left'|'right'} [props.align='left'] — Legacy; si hay `alignMenu`, se usa ese.
 * @param {'left'|'right'} [props.iconPosition='right']
 * @param {import('lucide-react').LucideIcon} [props.triggerIcon]
 * @param {string} [props.width='auto']
 * @param {string|null} [props.menuMaxHeight='280px'] — `none` = sin tope ni scroll.
 * @param {boolean} [props.usePortal=false] — renderiza el menú en portal (fixed) para evitar clipping/scrolls de contenedor.
 * @param {(open: boolean) => void} [props.onOpenChange] — notifica cuando el menú abre o cierra.
 * @param {string} [props.triggerAriaLabel] — Si no hay texto de `label` (vacío/omitido) pero sí `triggerIcon`, el botón es solo icono: se usa como `aria-label` y `title`.
 * @param {string} [props.triggerButtonCustomHeight] — Se pasa a `Button` como `customButtonHeight` (p. ej. `min-h-8` para alinear con toolbars).
 * @param {string} [props.triggerButtonClassName] — Clases extra en el `Button` integrado (solo aplica con trigger integrado; p. ej. padding solo en un consumidor concreto).
 */
const Dropdown = ({
  trigger,
  label,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  pill = false,
  menuPosition = 'bottom',
  alignMenu,
  align = 'left',
  iconPosition = 'right',
  triggerIcon: TriggerIcon,
  triggerAriaLabel = 'Open menu',
  triggerButtonCustomHeight,
  triggerButtonClassName = '',
  width = 'auto',
  menuMaxHeight = '280px',
  usePortal = false,
  onOpenChange,
  children,
  className = '',
  menuClassName = '',
}) => {
  const { isOpen, toggle, close } = useDisclosure();

  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const openedByKeyboardRef = useRef(false);
  const wasOpenRef = useRef(false);
  const [portalPos, setPortalPos] = useState({ top: 0, left: 0 });
  const [flipUp, setFlipUp] = useState(false);

  const alignResolved = alignMenu ?? align;
  const sizeResolved = annSizeToButtonSize(size);

  const ChevronIcon = isOpen ? ChevronUp : ChevronDown;

  const updatePortalPosition = useCallback(() => {
    if (!usePortal) return;
    const triggerRect = containerRef.current?.getBoundingClientRect();
    if (!triggerRect) return;
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth ?? 240;
    // The rendered height (capped by maxHeight), not scrollHeight: a long scrolling menu that
    // opens upward must sit against its trigger, not float at the top of the viewport.
    const menuHeight = menuEl?.offsetHeight ?? 0;
    const gap = 4;

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const openUp = menuPosition === 'top' || (menuPosition !== 'top' && spaceBelow < menuHeight + gap + 8);

    const top = openUp
      ? triggerRect.top - gap - menuHeight
      : triggerRect.bottom + gap;
    const rawLeft =
      alignResolved === 'right'
        ? triggerRect.right - menuWidth
        : triggerRect.left;
    const left = Math.max(
      8,
      Math.min(rawLeft, window.innerWidth - menuWidth - 8)
    );

    setPortalPos({ top: Math.max(8, top), left });
  }, [alignResolved, menuPosition, usePortal]);

  useEffect(() => {
    if (!isOpen) return;
    const listener = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      // Closing by a click elsewhere is not a keyboard close: the focus stays where the user put it.
      openedByKeyboardRef.current = false;
      close();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [isOpen, close]);

  // Esc cierra el menú y devuelve el foco al disparador. Se registra sólo
  // mientras está abierto, y no detiene la propagación, para no interferir con
  // el Esc de un modal o un slideout que lo contenga.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const active = document.activeElement;
        // Arrows belong to a text field or select placed inside the menu.
        if (active?.matches?.(TEXT_FIELD)) return;
        const inside = containerRef.current?.contains(active) || menuRef.current?.contains(active);
        const items = menuRef.current ? [...menuRef.current.querySelectorAll(FOCUSABLE_ITEM)] : [];
        if (!inside || items.length === 0) return;
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        const index = items.indexOf(active);
        const next = index === -1 ? (step === 1 ? 0 : items.length - 1) : (index + step + items.length) % items.length;
        items[next].focus();
        return;
      }
      if (e.key !== 'Escape') return;
      close();
      containerRef.current?.querySelector(TRIGGER_FOCUSABLE)?.focus?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  // Compute flip direction on mount and on window resize so the hidden panel
  // never extends the scroll area downward from the first render.
  useLayoutEffect(() => {
    if (menuPosition === 'top' || usePortal) return;
    const compute = () => {
      const triggerRect = containerRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      const menuEl = menuRef.current;
      const menuHeight = menuEl?.scrollHeight ?? menuEl?.offsetHeight ?? 200;
      setFlipUp(window.innerHeight - triggerRect.bottom < menuHeight + 8);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [menuPosition, usePortal]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    if (usePortal) {
      updatePortalPosition();
      const raf = requestAnimationFrame(updatePortalPosition);
      return () => cancelAnimationFrame(raf);
    }
    // Recalculate on open in case the card position changed due to scroll.
    if (menuPosition === 'top') return;
    const triggerRect = containerRef.current?.getBoundingClientRect();
    if (!triggerRect) return;
    const menuEl = menuRef.current;
    const menuHeight = menuEl?.scrollHeight ?? menuEl?.offsetHeight ?? 200;
    setFlipUp(window.innerHeight - triggerRect.bottom < menuHeight + 8);
  }, [isOpen, usePortal, menuPosition, children, updatePortalPosition]);

  useEffect(() => {
    if (!isOpen || !usePortal) return;
    const listener = () => updatePortalPosition();
    window.addEventListener('resize', listener);
    window.addEventListener('scroll', listener, true);
    return () => {
      window.removeEventListener('resize', listener);
      window.removeEventListener('scroll', listener, true);
    };
  }, [isOpen, usePortal, updatePortalPosition]);

  // A click with `detail === 0` comes from the keyboard (Enter/Space) or a programmatic or
  // screen-reader activation, which also deserve the focus: only then does the menu
  // take the focus, so a mouse user keeps theirs and a keyboard user is not left on the trigger.
  const handleToggle = useCallback(
    (event) => {
      if (disabled) return;
      openedByKeyboardRef.current = event?.detail === 0;
      toggle();
    },
    [disabled, toggle],
  );

  useEffect(() => {
    if (!isOpen) {
      // Picking a row unmounts the focused item (portal): give the focus back to the trigger.
      const lost = !document.activeElement || document.activeElement === document.body;
      if (wasOpenRef.current && openedByKeyboardRef.current && lost) {
        containerRef.current?.querySelector(TRIGGER_FOCUSABLE)?.focus?.();
      }
      wasOpenRef.current = false;
      return;
    }
    wasOpenRef.current = true;
    if (!openedByKeyboardRef.current) return;
    const raf = requestAnimationFrame(() => {
      menuRef.current?.querySelector(FOCUSABLE_ITEM)?.focus?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const verticalClass =
    menuPosition === 'top' || flipUp ? 'bottom-full mb-1' : 'top-full mt-1';

  const horizontalClass = alignResolved === 'right' ? 'right-0' : 'left-0';

  const mh = menuMaxHeight === 'none' ? undefined : menuMaxHeight ?? '280px';
  const overflowMenu = menuMaxHeight === 'none' ? 'overflow-visible' : 'overflow-y-auto';

  const menuStyleInline = {};
  if (width !== 'auto') menuStyleInline.width = width;
  if (mh) menuStyleInline.maxHeight = mh;

  const menuClass = usePortal
    ? [
        menuPanelPortalClass,
        overflowMenu,
        menuOpenClass,
        'transition-opacity duration-150 motion-reduce:transition-none',
        menuClassName,
      ]
        .filter(Boolean)
        .join(' ')
    : [
        menuPanelBaseClass,
        verticalClass,
        horizontalClass,
        overflowMenu,
        isOpen ? menuOpenClass : menuClosedClass,
        'transition-opacity duration-150 motion-reduce:transition-none',
        menuClassName,
      ]
        .filter(Boolean)
        .join(' ');

  const labelTrimmed =
    typeof label === 'string' ? label.trim() : label != null ? String(label).trim() : '';
  const hasTextLabel = labelTrimmed.length > 0;
  const useBuiltInTrigger = trigger == null && (hasTextLabel || TriggerIcon);

  const builtInTrigger = useBuiltInTrigger ? (
    <span className="inline-flex">
      <Button
        type="button"
        variant={variant}
        size={sizeResolved}
        disabled={disabled}
        pill={pill}
        customButtonHeight={triggerButtonCustomHeight}
        onClick={handleToggle}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={hasTextLabel ? undefined : triggerAriaLabel}
        title={hasTextLabel ? undefined : triggerAriaLabel}
        leftIcon={iconPosition === 'left' ? TriggerIcon || ChevronIcon : undefined}
        rightIcon={iconPosition === 'right' ? TriggerIcon || ChevronIcon : undefined}
        className={[
          'hover:border-accent/50',
          'focus-visible:border-accent/50',
          'focus-visible:ring-accent/50',
          typeof triggerButtonClassName === 'string' ? triggerButtonClassName.trim() : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {hasTextLabel ? label : null}
      </Button>
    </span>
  ) : null;

  const menuContextValue = useMemo(() => ({ close }), [close]);

  if (!builtInTrigger && trigger == null) {
    return null;
  }

  return (
    <DropdownMenuContext.Provider value={menuContextValue}>
      <div
        ref={containerRef}
        className={['relative inline-block', className].filter(Boolean).join(' ')}
      >
        {builtInTrigger ? (
          builtInTrigger
        ) : (
          <div
            onClick={handleToggle}
            className={[
              disabled ? 'pointer-events-none opacity-50' : 'cursor-pointer',
            ].join(' ')}
          >
            {trigger}
          </div>
        )}

        {usePortal
          ? isOpen &&
            createPortal(
              <div
                ref={menuRef}
                role="menu"
                aria-hidden={false}
                className={menuClass}
                style={{
                  ...menuStyleInline,
                  top: portalPos.top,
                  left: portalPos.left,
                }}
              >
                {children}
              </div>,
              document.body
            )
          : (
            <div
              ref={menuRef}
              role="menu"
              aria-hidden={!isOpen}
              className={menuClass}
              style={menuStyleInline}
            >
              {children}
            </div>
          )}
      </div>
    </DropdownMenuContext.Provider>
  );
};

const DropdownItem = ({
  children,
  icon: Icon,
  onClick,
  variant = 'default',
  disabled = false,
  className: itemClassName = '',
}) => {
  const ctx = useContext(DropdownMenuContext);
  const close = ctx?.close;

  const textClass =
    variant === 'danger'
      ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
      : 'text-content-secondary hover:bg-surface-hover-subtle';

  return (
    <button
      type="button"
      role="menuitem"
      onClick={(event) => {
        if (disabled) return;
        onClick?.(event);
        close?.();
      }}
      disabled={disabled}
      className={[
        'flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors',
        'disabled:pointer-events-none disabled:opacity-50',
        textClass,
        itemClassName,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
};

const DropdownSeparator = () => (
  <div className="my-1 border-t border-edge" role="separator" />
);

Dropdown.Item = DropdownItem;
Dropdown.Separator = DropdownSeparator;

/**
 * Access the parent `Dropdown` menu API from custom menu content (e.g. close on Apply).
 * @returns {{ close?: () => void } | null}
 */
export function useDropdownMenu() {
  return useContext(DropdownMenuContext);
}

export default Dropdown;
